import React, { useState, useMemo } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Calculator, RotateCcw, Download, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Local helpers — kept inline so this module is self-contained per the spec.
// ---------------------------------------------------------------------------
const fmtInr = (n) =>
  isFinite(n)
    ? `₹ ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
const fmtPct = (n) => (isFinite(n) ? `${Number(n).toFixed(2)}%` : "—");
const fmtCount = (n) => (isFinite(n) ? Number(n).toLocaleString("en-IN") : "—");
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const DEFAULTS = {
  ad_spend: "",
  total_orders: "",
  total_revenue: "",
  delivery_pct: "",
  cogs_per_order: "",
  shipping_per_order: "",
  packaging_per_order: "",
  rto_cost_per_order: "",
  rto_inventory_per_order: "",
  overheads: "",
  gst_rate: "18",
  resold_inventory: "0",
};

// Numeric input — INR or %, validates non-negative; orders forced to integer.
function NumberField({
  id,
  label,
  prefix,
  suffix,
  value,
  onChange,
  helper,
  integer = false,
  min = 0,
  max,
  error,
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="text-[11px] uppercase tracking-[0.18em] text-[#b89846]/85 font-bold block"
      >
        {label}
      </label>
      <div
        className={[
          "flex items-stretch rounded-xl border-[1.5px] bg-[#0F0F0E] overflow-hidden",
          error ? "border-red-500/60" : "border-[#b89846]/35 focus-within:border-[#b89846]/70",
          "transition-colors",
        ].join(" ")}
      >
        {prefix ? (
          <span className="inline-flex items-center px-3 text-sm text-[#b89846]/85 border-r border-[#b89846]/20 select-none">
            {prefix}
          </span>
        ) : null}
        <input
          id={id}
          data-testid={`pnl-input-${id}`}
          inputMode="decimal"
          type="number"
          step={integer ? "1" : "any"}
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm md:text-[15px] text-white placeholder:text-white/30 outline-none"
          placeholder="0"
        />
        {suffix ? (
          <span className="inline-flex items-center px-3 text-sm text-[#b89846]/85 border-l border-[#b89846]/20 select-none">
            {suffix}
          </span>
        ) : null}
      </div>
      {helper && !error ? (
        <div className="text-[11px] text-white/45 leading-snug">{helper}</div>
      ) : null}
      {error ? (
        <div className="text-[11px] text-red-300/90 leading-snug" data-testid={`pnl-error-${id}`}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <section className="rounded-2xl border border-[#b89846]/35 bg-[#0B0B0A] overflow-hidden">
      <header className="px-5 md:px-6 py-3 border-b border-[#b89846]/20 bg-[#141210]">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#b89846]/75 font-bold">
          {eyebrow}
        </div>
        <div className="text-base md:text-[17px] text-white font-semibold leading-snug">
          {title}
        </div>
      </header>
      <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">{children}</div>
    </section>
  );
}

function SummaryCard({ label, value, trend, testId }) {
  const positive = trend === "positive";
  const negative = trend === "negative";
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-[#b89846]/35 bg-[#0F0F0E] p-5 md:p-6 shadow-[0_10px_30px_-18px_rgba(184,152,70,0.35)]"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold mb-1.5">
        {label}
      </div>
      <div
        className={[
          "text-xl md:text-[26px] font-semibold tracking-tight",
          positive ? "text-emerald-400" : negative ? "text-red-400" : "text-white",
        ].join(" ")}
      >
        {value}
      </div>
      {trend ? (
        <div className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-bold">
          {positive ? (
            <>
              <TrendingUp size={12} strokeWidth={2.4} className="text-emerald-400" />
              <span className="text-emerald-400/90">Profitable</span>
            </>
          ) : negative ? (
            <>
              <TrendingDown size={12} strokeWidth={2.4} className="text-red-400" />
              <span className="text-red-400/90">In the red</span>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function PnLCalculator() {
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  const update = (key) => (val) =>
    setForm((f) => ({ ...f, [key]: val }));

  // Number parser — empty string => 0; rejects negatives & NaN.
  const num = (raw) => {
    if (raw === "" || raw === null || raw === undefined) return 0;
    const n = Number(raw);
    return isFinite(n) ? n : 0;
  };

  const validate = () => {
    const e = {};
    const orders = Number(form.total_orders);
    const revenue = Number(form.total_revenue);
    const delivery = Number(form.delivery_pct);
    if (!form.total_orders || !Number.isFinite(orders) || orders < 1 || !Number.isInteger(orders)) {
      e.total_orders = "Total orders must be a positive whole number.";
    }
    if (!form.total_revenue || !Number.isFinite(revenue) || revenue <= 0) {
      e.total_revenue = "Total revenue must be greater than zero.";
    }
    if (form.delivery_pct === "" || !Number.isFinite(delivery) || delivery < 0 || delivery > 100) {
      e.delivery_pct = "Delivery % must be between 0 and 100.";
    }
    const nonNegFields = [
      ["ad_spend", "Ad Spend"],
      ["cogs_per_order", "COGS per delivered order"],
      ["shipping_per_order", "Shipping per delivered order"],
      ["packaging_per_order", "Packaging per order"],
      ["rto_cost_per_order", "RTO cost per returned order"],
      ["rto_inventory_per_order", "RTO inventory loss per returned order"],
      ["overheads", "Overheads"],
      ["gst_rate", "GST rate"],
      ["resold_inventory", "Resold Inventory"],
    ];
    for (const [k, label] of nonNegFields) {
      if (form[k] !== "" && Number(form[k]) < 0) {
        e[k] = `${label} cannot be negative.`;
      }
    }
    if (form.gst_rate !== "" && Number(form.gst_rate) > 100) {
      e.gst_rate = "GST rate cannot exceed 100%.";
    }
    return e;
  };

  const compute = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please fix the highlighted fields.");
      setResult(null);
      return;
    }

    const total_orders = Number(form.total_orders);
    const total_revenue = Number(form.total_revenue);
    const delivery_pct = Number(form.delivery_pct);
    const ad_spend = num(form.ad_spend);
    const cogs_per_order = num(form.cogs_per_order);
    const shipping_per_order = num(form.shipping_per_order);
    const packaging_per_order = num(form.packaging_per_order);
    const rto_cost_per_order = num(form.rto_cost_per_order);
    const rto_inventory_per_order = num(form.rto_inventory_per_order);
    const overheads = num(form.overheads);
    const gst_rate = num(form.gst_rate);
    const resold_inventory = num(form.resold_inventory);

    // Calculation engine — order per spec.
    const delivered_orders = Math.round((delivery_pct / 100) * total_orders);
    const rto_orders = total_orders - delivered_orders;
    const delivered_revenue = total_revenue * (delivery_pct / 100);
    // Guard divide-by-zero (already ensured orders>0 and revenue>0 by validation,
    // but keep the guard for safety).
    const aov = total_orders > 0 ? total_revenue / total_orders : 0;
    const cpa = total_orders > 0 ? ad_spend / total_orders : 0;
    const cogs = delivered_orders * cogs_per_order;
    const shipping = delivered_orders * shipping_per_order;
    const packaging = total_orders * packaging_per_order;
    const rto_cost = rto_orders * rto_cost_per_order;
    const rto_inventory = rto_orders * rto_inventory_per_order;
    const gross_profit =
      delivered_revenue - ad_spend - cogs - shipping - rto_inventory - rto_cost - packaging;
    const net_profit = gross_profit - overheads;
    const gst = net_profit * (gst_rate / 100);
    const final_profit = net_profit - gst + resold_inventory;
    const net_profit_pct = total_revenue > 0 ? (final_profit / total_revenue) * 100 : 0;

    setResult({
      // Inputs (echoed in the statement table)
      ad_spend: round2(ad_spend),
      total_orders,
      total_revenue: round2(total_revenue),
      delivery_pct: round2(delivery_pct),
      // Derived top-line
      delivered_revenue: round2(delivered_revenue),
      aov: round2(aov),
      cpa: round2(cpa),
      delivered_orders,
      rto_orders,
      // Cost blocks
      cogs: round2(cogs),
      shipping: round2(shipping),
      packaging: round2(packaging),
      rto_cost: round2(rto_cost),
      rto_inventory: round2(rto_inventory),
      // P&L
      gross_profit: round2(gross_profit),
      overheads: round2(overheads),
      net_profit: round2(net_profit),
      gst: round2(gst),
      resold_inventory: round2(resold_inventory),
      final_profit: round2(final_profit),
      net_profit_pct: round2(net_profit_pct),
    });
    toast.success("P&L calculated.");
  };

  const reset = () => {
    setForm(DEFAULTS);
    setResult(null);
    setErrors({});
  };

  // Statement order per spec — used by both the table and the CSV export.
  const STATEMENT_ROWS = useMemo(
    () => [
      { key: "ad_spend", label: "Ad Spend", kind: "inr" },
      { key: "total_orders", label: "Total Orders", kind: "count" },
      { key: "total_revenue", label: "Total Revenue", kind: "inr" },
      { key: "delivery_pct", label: "Delivery %", kind: "pct" },
      { key: "delivered_revenue", label: "Delivered Revenue", kind: "inr" },
      { key: "aov", label: "AOV", kind: "inr" },
      { key: "cpa", label: "CPA", kind: "inr" },
      { key: "delivered_orders", label: "Delivered Orders", kind: "count" },
      { key: "rto_orders", label: "RTO Orders", kind: "count" },
      { key: "cogs", label: "COGS", kind: "inr" },
      { key: "shipping", label: "Shipping Cost", kind: "inr" },
      { key: "packaging", label: "Packaging Cost", kind: "inr" },
      { key: "rto_cost", label: "RTO Cost", kind: "inr" },
      { key: "rto_inventory", label: "RTO Inventory Loss", kind: "inr" },
      { key: "gross_profit", label: "Gross Profit", kind: "inr", emphasis: true },
      { key: "overheads", label: "Overheads", kind: "inr" },
      { key: "net_profit", label: "Net Profit", kind: "inr", emphasis: true },
      { key: "gst", label: "GST", kind: "inr" },
      { key: "resold_inventory", label: "Resold Inventory", kind: "inr" },
      { key: "final_profit", label: "Final Profit", kind: "inr", emphasis: true, color: true },
      { key: "net_profit_pct", label: "Net Profit %", kind: "pct", emphasis: true, color: true },
    ],
    []
  );

  const formatCell = (kind, v) =>
    kind === "inr" ? fmtInr(v) : kind === "pct" ? fmtPct(v) : fmtCount(v);

  const downloadCsv = () => {
    if (!result) return;
    const lines = ['"Metric","Value (INR)"'];
    for (const row of STATEMENT_ROWS) {
      const v = result[row.key];
      // Spec: currency as plain numbers (no ₹, no commas), percentages as numbers.
      const num = typeof v === "number" ? v : Number(v);
      const valStr = Number.isFinite(num) ? String(num) : "";
      const safeLabel = String(row.label).replace(/"/g, '""');
      lines.push(`"${safeLabel}","${valStr}"`);
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const yyyyMmDd = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `pnl_${yyyyMmDd}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const finalTrend = result
    ? result.final_profit > 0
      ? "positive"
      : result.final_profit < 0
        ? "negative"
        : null
    : null;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div
              data-testid="pnl-eyebrow"
              className="text-[11px] uppercase tracking-[0.28em] text-[#b89846]/85 font-bold mb-2"
            >
              Operator Tools
            </div>
            <h1
              data-testid="pnl-title"
              className="text-3xl md:text-[40px] font-semibold tracking-tight text-white leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, system-ui, sans-serif" }}
            >
              P&L Calculator
            </h1>
            <p className="mt-2 text-sm md:text-[15px] text-white/65 max-w-2xl leading-relaxed">
              Plug in your ad spend, orders, revenue and per-unit costs to see your real Indian
              D2C profit — gross, net, post-GST final profit and net profit %. Export the full
              statement as CSV when you&apos;re done.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="pnl-reset-btn"
              onClick={reset}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-[12px] uppercase tracking-[0.18em] font-bold text-[#b89846] border-[1.5px] border-[#b89846]/35 hover:bg-[#b89846]/10 hover:border-[#b89846]/60 transition"
            >
              <RotateCcw size={14} strokeWidth={2.2} />
              Reset
            </button>
            <button
              type="button"
              data-testid="pnl-calc-btn"
              onClick={compute}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[12px] uppercase tracking-[0.18em] font-bold transition-all"
              style={{
                background:
                  "linear-gradient(180deg, #e1c074 0%, #b89846 55%, #8a6f2a 100%)",
                color: "#0b0b0a",
                boxShadow:
                  "0 12px 28px -10px rgba(184,152,70,0.6), inset 0 1px 0 rgba(255,255,255,0.45)",
              }}
            >
              <Calculator size={14} strokeWidth={2.4} />
              Calculate P&L
            </button>
          </div>
        </div>

        {/* Input sections */}
        <div className="space-y-6">
          <Section eyebrow="Section A" title="Top-line">
            <NumberField
              id="ad_spend"
              label="Ad Spend"
              prefix="₹"
              value={form.ad_spend}
              onChange={update("ad_spend")}
              error={errors.ad_spend}
            />
            <NumberField
              id="total_orders"
              label="Total Orders"
              value={form.total_orders}
              onChange={update("total_orders")}
              integer
              min={1}
              helper="Whole positive number."
              error={errors.total_orders}
            />
            <NumberField
              id="total_revenue"
              label="Total Revenue"
              prefix="₹"
              value={form.total_revenue}
              onChange={update("total_revenue")}
              error={errors.total_revenue}
            />
            <NumberField
              id="delivery_pct"
              label="Delivery %"
              suffix="%"
              max={100}
              value={form.delivery_pct}
              onChange={update("delivery_pct")}
              helper="0–100 — share of total orders that successfully deliver."
              error={errors.delivery_pct}
            />
          </Section>

          <Section eyebrow="Section B" title="Per-unit Costs">
            <NumberField
              id="cogs_per_order"
              label="COGS per delivered order"
              prefix="₹"
              value={form.cogs_per_order}
              onChange={update("cogs_per_order")}
              error={errors.cogs_per_order}
            />
            <NumberField
              id="shipping_per_order"
              label="Shipping cost per delivered order"
              prefix="₹"
              value={form.shipping_per_order}
              onChange={update("shipping_per_order")}
              error={errors.shipping_per_order}
            />
            <NumberField
              id="packaging_per_order"
              label="Packaging cost per order"
              prefix="₹"
              value={form.packaging_per_order}
              onChange={update("packaging_per_order")}
              helper="Applies to ALL orders (delivered + RTO)."
              error={errors.packaging_per_order}
            />
            <NumberField
              id="rto_cost_per_order"
              label="RTO cost per returned order"
              prefix="₹"
              value={form.rto_cost_per_order}
              onChange={update("rto_cost_per_order")}
              helper="Reverse shipping, handling, COD remit fees."
              error={errors.rto_cost_per_order}
            />
            <NumberField
              id="rto_inventory_per_order"
              label="RTO inventory loss per returned order"
              prefix="₹"
              value={form.rto_inventory_per_order}
              onChange={update("rto_inventory_per_order")}
              helper="Damaged/lost stock cost per RTO."
              error={errors.rto_inventory_per_order}
            />
          </Section>

          <Section eyebrow="Section C" title="Fixed & Adjustments">
            <NumberField
              id="overheads"
              label="Overheads"
              prefix="₹"
              value={form.overheads}
              onChange={update("overheads")}
              error={errors.overheads}
            />
            <NumberField
              id="gst_rate"
              label="GST rate"
              suffix="%"
              max={100}
              value={form.gst_rate}
              onChange={update("gst_rate")}
              helper="Defaults to 18%."
              error={errors.gst_rate}
            />
            <NumberField
              id="resold_inventory"
              label="Resold Inventory recovered"
              prefix="₹"
              value={form.resold_inventory}
              onChange={update("resold_inventory")}
              helper="Revenue recovered by reselling RTO stock."
              error={errors.resold_inventory}
            />
          </Section>
        </div>

        {/* Results */}
        {result ? (
          <div className="mt-10 space-y-6" data-testid="pnl-results">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard
                testId="pnl-summary-gross-profit"
                label="Gross Profit"
                value={fmtInr(result.gross_profit)}
              />
              <SummaryCard
                testId="pnl-summary-net-profit"
                label="Net Profit"
                value={fmtInr(result.net_profit)}
              />
              <SummaryCard
                testId="pnl-summary-final-profit"
                label="Final Profit"
                value={fmtInr(result.final_profit)}
                trend={finalTrend}
              />
              <SummaryCard
                testId="pnl-summary-net-profit-pct"
                label="Net Profit %"
                value={fmtPct(result.net_profit_pct)}
                trend={finalTrend}
              />
            </div>

            {/* Statement table */}
            <div className="rounded-2xl border border-[#b89846]/35 bg-[#0F0F0E] overflow-hidden">
              <header className="flex items-center justify-between gap-3 px-5 md:px-6 py-3 border-b border-[#b89846]/25 bg-[#141210]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#b89846]/75 font-bold">
                    Statement
                  </div>
                  <div className="text-base md:text-[17px] text-white font-semibold leading-snug">
                    Full P&L Breakdown
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="pnl-download-csv-btn"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold transition-all"
                  style={{
                    background:
                      "linear-gradient(180deg, #e1c074 0%, #b89846 55%, #8a6f2a 100%)",
                    color: "#0b0b0a",
                    boxShadow:
                      "0 10px 22px -10px rgba(184,152,70,0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
                  }}
                >
                  <Download size={13} strokeWidth={2.4} />
                  Download CSV
                </button>
              </header>
              <table className="w-full text-sm" data-testid="pnl-statement-table">
                <thead className="sr-only">
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {STATEMENT_ROWS.map((row, i) => {
                    const v = result[row.key];
                    const isPositive = row.color && Number(v) > 0;
                    const isNegative = row.color && Number(v) < 0;
                    return (
                      <tr
                        key={row.key}
                        data-testid={`pnl-row-${row.key}`}
                        className={[
                          i % 2 === 0 ? "bg-transparent" : "bg-[#141210]/50",
                          row.emphasis ? "" : "",
                        ].join(" ")}
                      >
                        <td
                          className={[
                            "px-5 md:px-6 py-3 border-t border-[#b89846]/12 align-middle",
                            row.emphasis ? "text-white font-semibold" : "text-white/80",
                          ].join(" ")}
                        >
                          {row.label}
                        </td>
                        <td
                          className={[
                            "px-5 md:px-6 py-3 border-t border-[#b89846]/12 align-middle text-right tabular-nums",
                            row.emphasis ? "text-[15px] md:text-[16px] font-semibold" : "text-white/85",
                            isPositive ? "text-emerald-400" : isNegative ? "text-red-400" : "",
                          ].join(" ")}
                        >
                          {formatCell(row.kind, v)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div
            data-testid="pnl-empty-state"
            className="mt-10 rounded-2xl border border-dashed border-[#b89846]/30 bg-[#0F0F0E]/60 px-6 py-10 text-center"
          >
            <Calculator size={28} strokeWidth={1.6} className="mx-auto text-[#b89846]/70 mb-3" />
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold mb-1">
              Awaiting inputs
            </div>
            <div className="text-sm text-white/60 max-w-md mx-auto">
              Fill in the three sections above and hit <span className="text-[#e8c879] font-semibold">Calculate P&L</span> to see your full profit statement and unlock the CSV export.
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
